import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  ArrowUpRight,
  BellRing,
  CreditCard,
  Gift,
  Link2,
  Smartphone,
  Sparkles,
  UserPlus,
  UserRound,
  Users as UsersIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/Layout';
import { TrendChart } from '../components/TrendChart';
import {
  Card,
  CardHeader,
  ErrorNote,
  Skeleton,
  cn,
} from '../components/ui';
import { api } from '../lib/api';
import {
  formatNumber,
  formatPlatform,
  percentOf,
  timezoneOffsetMinutes,
} from '../lib/format';
import type {
  AccountFunnel,
  AccountFunnelOverview,
  DashboardOverview,
  PackageBreakdown,
  SubscriptionEventType,
} from '../lib/types';
import { subscriptionEventLink, usersLink } from '../lib/user-links';

const TREND_DAYS = 14;

export function DashboardPage() {
  const overview = useQuery({
    queryKey: ['dashboard', TREND_DAYS],
    queryFn: () =>
      api.get<DashboardOverview>('/api/dashboard', {
        // "Today" must mean the operator's day, not the container's UTC day.
        tz_offset: timezoneOffsetMinutes(),
        days: TREND_DAYS,
      }),
    // The backend memoises this for a minute; refreshing more often would just
    // return the same payload.
    refetchInterval: 60_000,
  });

  return (
    <>
      <PageHeader
        title="Dashboard"
        description="Today's activity across every app"
      />

      {overview.isError && <ErrorNote error={overview.error} />}

      {overview.isPending ? (
        <DashboardSkeleton />
      ) : overview.data ? (
        <div className="space-y-5">
          <StatGrid data={overview.data} />

          <Card>
            <CardHeader
              title={`New devices and purchases — last ${TREND_DAYS} days`}
              description="Registrations and store events per day"
            />
            <div className="p-4 sm:p-5">
              <TrendChart data={overview.data.trend} />
            </div>
          </Card>

          <TodayEvents
            events={overview.data.today_events}
            todayStart={overview.data.today_start}
          />

          <AccountFunnelCard />

          <div className="grid gap-4 xl:grid-cols-2">
            {overview.data.apps.map((app) => (
              <AppCard
                key={app.app_key ?? app.app_label}
                appKey={app.app_key}
                label={app.app_label}
                total={app.total}
                platforms={app.platforms}
                todayStart={overview.data.today_start}
              />
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

// ── Headline tiles ──────────────────────────────────────────────────────────

/**
 * Every tile opens the Users page filtered to the devices it counts. "Today"
 * is the backend's own `today_start`, so the list and the number it came
 * from always draw the day boundary in the same place.
 */
function StatGrid({ data }: { data: DashboardOverview }) {
  const { totals, today_events: events, today_start: today } = data;
  const subscribedToday = events.purchase ?? 0;

  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
      {/* Today first — the numbers an operator opens this page for. */}
      <StatTile
        icon={<UserPlus size={16} />}
        label="New today"
        value={totals.new_today}
        tone="brand"
        to={usersLink({ registered_from: today })}
      />
      <StatTile
        icon={<CreditCard size={16} />}
        label="Subscribed today"
        value={subscribedToday}
        tone="brand"
        to={subscriptionEventLink('purchase', today)}
        caption={
          events.renewal ? `+${formatNumber(events.renewal)} renewals` : undefined
        }
      />
      <StatTile
        icon={<Activity size={16} />}
        label="Active today"
        value={totals.active_today}
        to={usersLink({ last_seen_from: today }, 'last_seen')}
        caption={`${percentOf(totals.active_today, totals.users)} of all devices`}
      />
      <StatTile
        icon={<UsersIcon size={16} />}
        label="Total devices"
        value={totals.users}
        to={usersLink({})}
      />

      <StatTile
        icon={<CreditCard size={16} />}
        label="Premium"
        value={totals.premium}
        to={usersLink({ is_premium: true })}
        caption={percentOf(totals.premium, totals.users)}
      />
      <StatTile
        icon={<Gift size={16} />}
        label="Gifted"
        value={totals.gift}
        to={usersLink({ is_gift: true })}
      />
      <StatTile
        icon={<Sparkles size={16} />}
        label="On trial"
        value={totals.trial}
        to={usersLink({ is_trial: true })}
      />
      <StatTile
        icon={<BellRing size={16} />}
        label="Push-ready"
        value={totals.with_fcm_token}
        to={usersLink({ has_fcm_token: true })}
        caption="devices with an FCM token"
      />
    </div>
  );
}

function StatTile({
  icon,
  label,
  value,
  caption,
  tone = 'neutral',
  to,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  caption?: string;
  tone?: 'neutral' | 'brand';
  /** The Users page filtered to exactly the devices this tile counts. */
  to: string;
}) {
  return (
    <Link
      to={to}
      className={cn(
        'group block rounded-xl border p-3.5 shadow-sm transition-colors sm:p-4',
        tone === 'brand'
          ? 'border-brand/25 bg-brand-soft hover:border-brand/50'
          : 'border-line bg-surface hover:border-line-strong hover:bg-raised',
      )}
    >
      <div
        className={cn(
          'flex items-center gap-1.5 text-xs font-medium',
          tone === 'brand' ? 'text-brand' : 'text-ink-2',
        )}
      >
        {icon}
        <span className="truncate">{label}</span>
        <ArrowUpRight
          size={13}
          className="ml-auto shrink-0 opacity-0 transition-opacity group-hover:opacity-70 group-focus-visible:opacity-70"
        />
      </div>
      <p className="mt-2 text-2xl font-semibold tracking-tight text-ink">
        {formatNumber(value)}
      </p>
      {caption && <p className="mt-0.5 text-xs text-ink-3">{caption}</p>}
    </Link>
  );
}

// ── Today's store events ────────────────────────────────────────────────────

/** Labels for the subscription event types worth surfacing on the dashboard. */
const EVENT_LABELS: Record<SubscriptionEventType, string> = {
  purchase: 'Purchases',
  renewal: 'Renewals',
  trial: 'Trials started',
  restore: 'Restores',
  cancel: 'Cancellations',
  expire: 'Expirations',
  billing_issue: 'Billing issues',
  uncancel: 'Re-subscribed',
};

function TodayEvents({
  events,
  todayStart,
}: {
  events: Record<string, number>;
  todayStart: string;
}) {
  const entries = (
    Object.entries(EVENT_LABELS) as [SubscriptionEventType, string][]
  ).filter(([type]) => (events[type] ?? 0) > 0);

  return (
    <Card>
      <CardHeader
        title="Subscription events today"
        description="Store activity recorded since midnight"
      />
      {entries.length === 0 ? (
        <p className="px-4 py-6 text-sm text-ink-3 sm:px-5">
          No store events recorded yet today.
        </p>
      ) : (
        <div className="grid grid-cols-2 divide-line sm:grid-cols-4">
          {entries.map(([type, label]) => (
            <Link
              key={type}
              to={subscriptionEventLink(type, todayStart)}
              className="border-b border-line px-4 py-3 transition-colors last:border-b-0 hover:bg-raised sm:px-5 sm:[&:nth-last-child(-n+4)]:border-b-0"
            >
              <span className="block text-xs text-ink-2">{label}</span>
              <span className="mt-1 block text-lg font-semibold text-ink">
                {formatNumber(events[type] ?? 0)}
              </span>
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Account setup funnel ────────────────────────────────────────────────────

/**
 * How far installs get through account setup, per app.
 *
 * The number that says whether the app is landing at all: a low connected rate
 * means people install and never get through the QR scan, which is a product
 * problem rather than a marketing one. The `stalled` column is the audience an
 * automation rule is worth pointing at.
 */
function AccountFunnelCard() {
  const funnel = useQuery({
    queryKey: ['account-funnel'],
    queryFn: () =>
      api.get<AccountFunnelOverview>('/api/accounts/stats', {
        tz_offset: timezoneOffsetMinutes(),
      }),
    // Memoised for a minute upstream, same as the dashboard's own aggregates.
    refetchInterval: 60_000,
  });

  if (funnel.isPending) return <Skeleton className="h-56" />;
  if (funnel.isError) return <ErrorNote error={funnel.error} />;
  if (!funnel.data) return null;

  const { totals, apps } = funnel.data;

  return (
    <Card>
      <CardHeader
        title="Account setup"
        description="Of the devices that installed, how many named an account and how many linked one"
        action={
          <span className="text-xs text-ink-3">
            {formatNumber(totals.connected_today)} linked today
          </span>
        }
      />

      <div className="grid gap-3 p-4 sm:grid-cols-3 sm:p-5">
        <FunnelStage
          icon={<UsersIcon size={14} />}
          label="Installed"
          value={totals.devices}
          caption="every registered device"
          to={usersLink({})}
        />
        <FunnelStage
          icon={<UserRound size={14} />}
          label="Named an account"
          value={totals.devices_named}
          caption={`${totals.named_rate}% of installs`}
          to={usersLink({ has_named_account: true })}
        />
        <FunnelStage
          icon={<Link2 size={14} />}
          label="Linked an account"
          value={totals.devices_connected}
          caption={`${totals.connected_rate}% of installs`}
          tone="good"
          to={usersLink({ has_connected_account: true })}
        />
      </div>

      <div className="divide-y divide-line border-t border-line">
        {apps.map((app) => (
          <AppFunnelRow key={app.app_key ?? app.app_label} app={app} />
        ))}
      </div>

      {totals.stalled > 0 && (
        <Link
          to={usersLink({
            has_named_account: true,
            has_connected_account: false,
          })}
          className="block border-t border-line px-4 py-2.5 text-xs text-ink-3 transition-colors hover:bg-raised hover:text-ink-2 sm:px-5"
        >
          {formatNumber(totals.stalled)} device
          {totals.stalled === 1 ? '' : 's'} named an account and never linked
          one — the audience a “finish linking” automation is for.
        </Link>
      )}
    </Card>
  );
}

function FunnelStage({
  icon,
  label,
  value,
  caption,
  tone = 'neutral',
  to,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  caption: string;
  tone?: 'neutral' | 'good';
  to: string;
}) {
  return (
    <Link
      to={to}
      className={cn(
        'block rounded-xl border p-3.5 transition-colors',
        tone === 'good'
          ? 'border-good/25 bg-good-soft hover:border-good/50'
          : 'border-line bg-raised hover:border-line-strong',
      )}
    >
      <div
        className={cn(
          'flex items-center gap-1.5 text-xs font-medium',
          tone === 'good' ? 'text-good' : 'text-ink-2',
        )}
      >
        {icon}
        {label}
      </div>
      <p className="mt-1.5 text-2xl font-semibold tracking-tight text-ink">
        {formatNumber(value)}
      </p>
      <p className="mt-0.5 text-xs text-ink-3">{caption}</p>
    </Link>
  );
}

function AppFunnelRow({ app }: { app: AccountFunnel }) {
  return (
    <div className="px-4 py-3 sm:px-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-ink">{app.app_label}</span>
        <span className="tnum text-xs text-ink-3">
          {formatNumber(app.devices_connected)} of {formatNumber(app.devices)}{' '}
          linked · {app.connected_rate}%
        </span>
      </div>

      {/* Two bars on one track: named is the wider, linked sits inside it, so
          the gap between them reads as the drop-off it is. */}
      <div className="relative mt-2 h-1.5 overflow-hidden rounded-full bg-raised">
        <div
          className="absolute inset-y-0 left-0 bg-brand/35"
          style={{ width: `${Math.min(app.named_rate, 100)}%` }}
        />
        <div
          className="absolute inset-y-0 left-0 bg-good"
          style={{ width: `${Math.min(app.connected_rate, 100)}%` }}
        />
      </div>

      <p className="mt-1.5 text-xs text-ink-3">
        {formatNumber(app.devices_named)} named ({app.named_rate}%) ·{' '}
        {formatNumber(app.stalled)} stalled · {app.connected_of_named_rate}% of
        those who started got linked
      </p>
    </div>
  );
}

// ── Per-app breakdown ───────────────────────────────────────────────────────

function AppCard({
  appKey,
  label,
  total,
  platforms,
  todayStart,
}: {
  appKey: DashboardOverview['apps'][number]['app_key'];
  label: string;
  total: number;
  platforms: PackageBreakdown[];
  todayStart: string;
}) {
  return (
    <Card>
      <CardHeader
        title={label}
        description={`${formatNumber(total)} devices`}
        action={
          appKey && (
            <Link
              to={usersLink({ app_key: appKey })}
              className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
            >
              View devices
              <ArrowUpRight size={13} />
            </Link>
          )
        }
      />
      <div className="divide-y divide-line">
        {platforms.length === 0 && (
          <p className="px-4 py-6 text-sm text-ink-3 sm:px-5">
            No devices registered yet.
          </p>
        )}
        {platforms.map((row) => {
          // The exact build and platform this row counts; `app_key` is left
          // out because the package name already pins the app.
          const scope = {
            app_package_name: row.app_package_name,
            platform: row.platform,
          };
          return (
            <div
              key={`${row.app_package_name}:${row.platform}`}
              className="px-4 py-3 sm:px-5"
            >
              <Link
                to={usersLink(scope)}
                className="flex items-center justify-between gap-3 rounded-md hover:text-brand"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <Smartphone size={14} className="shrink-0 text-ink-3" />
                  <span className="text-sm font-medium text-ink">
                    {formatPlatform(row.platform)}
                  </span>
                </div>
                <span className="tnum text-sm font-semibold text-ink">
                  {formatNumber(row.total)}
                </span>
              </Link>

              <p className="mt-1 truncate text-xs text-ink-3" title={row.app_package_name}>
                {row.app_package_name}
              </p>

              <div className="mt-2.5 grid grid-cols-4 gap-2 text-xs">
                <MiniStat
                  label="New"
                  value={row.new_today}
                  to={usersLink({ ...scope, registered_from: todayStart })}
                />
                <MiniStat
                  label="Premium"
                  value={row.premium}
                  to={usersLink({ ...scope, is_premium: true })}
                />
                <MiniStat
                  label="Gift"
                  value={row.gift}
                  to={usersLink({ ...scope, is_gift: true })}
                />
                <MiniStat
                  label="FCM"
                  value={row.with_fcm_token}
                  icon={<BellRing size={11} />}
                  to={usersLink({ ...scope, has_fcm_token: true })}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function MiniStat({
  label,
  value,
  icon,
  to,
}: {
  label: string;
  value: number;
  icon?: ReactNode;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="block rounded-lg bg-raised px-2 py-1.5 transition-colors hover:bg-brand-soft"
    >
      <span className="flex items-center gap-1 text-[11px] text-ink-3">
        {icon}
        {label}
      </span>
      <span className="tnum mt-0.5 block font-medium text-ink">
        {formatNumber(value)}
      </span>
    </Link>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {Array.from({ length: 8 }, (_, index) => (
          <Skeleton key={index} className="h-[92px]" />
        ))}
      </div>
      <Skeleton className="h-80" />
      <Skeleton className="h-32" />
    </div>
  );
}
